import React, { useState } from 'react';
import styled, { useTheme } from 'styled-components';
import { Container, ContentRow, Header } from '@src/components/Atoms/Modal';
import ModalContainer from '@src/components/Molecules/ModalContainer';
import CMButton from '@src/components/Atoms/CMButton';
import CMText from '@src/components/Atoms/CMText';

// 백엔드 WithdrawalReason enum 과 값 일치
const REASONS: { value: string; label: string }[] = [
  { value: 'NOT_USING', label: '잘 사용하지 않아요' },
  { value: 'PRIVACY_CONCERN', label: '개인정보가 걱정돼요' },
  { value: 'SWITCH_ACCOUNT', label: '계정을 새로 만들고 싶어요' },
  { value: 'LACK_OF_CONTENT', label: '콘텐츠가 부족해요' },
  { value: 'INCONVENIENT', label: '이용이 불편해요' },
  { value: 'ETC', label: '기타' },
];

interface Props {
  onClose: () => void;
  // 성공 여부를 반환(true=탈퇴 성공). 실패 시 모달을 유지하고 재시도할 수 있게 한다.
  onConfirm: (reason?: string, detail?: string) => Promise<boolean>;
  onDone: () => void;
}

type Step = 'form' | 'confirm' | 'done';

const WithdrawalModal = ({ onClose, onConfirm, onDone }: Props) => {
  const theme = useTheme();
  const [step, setStep] = useState<Step>('form');
  const [reason, setReason] = useState<string>('');
  const [detail, setDetail] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string>('');

  const handleSubmit = async () => {
    if (submitting) return; // 이중 클릭 방지
    setSubmitting(true);
    setError('');
    try {
      const ok = await onConfirm(reason || undefined, detail || undefined);
      if (ok) {
        setStep('done');
      } else {
        setError('탈퇴 처리에 실패했어요. 잠시 후 다시 시도해 주세요.');
      }
    } catch {
      setError('탈퇴 처리에 실패했어요. 잠시 후 다시 시도해 주세요.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalContainer
      width={480}
      height={520}
      onClose={submitting ? () => {} : step === 'done' ? onDone : onClose}
      stopPropagation={(e) => {
        e.stopPropagation();
      }}
      backgroundColor={theme.commonColor.white}
    >
      <Container>
        <Header>
          <HeaderTitle fontSize={22} fontWeight={700}>
            {step === 'done' ? '탈퇴 완료' : '회원 탈퇴'}
          </HeaderTitle>
        </Header>

        {step === 'form' && (
          <>
            <Content>
              <Description>
                탈퇴하면 개인정보는 즉시 파기되며 되돌릴 수 없습니다. 작성하신
                게시글·댓글·사연·감사편지는 &apos;피플러&apos;로 익명화되어
                남습니다.
              </Description>
              <ReasonList>
                {REASONS.map((r) => (
                  <ReasonItem key={r.value} htmlFor={`reason-${r.value}`}>
                    <input
                      type="radio"
                      id={`reason-${r.value}`}
                      name="withdrawal-reason"
                      value={r.value}
                      checked={reason === r.value}
                      onChange={() => setReason(r.value)}
                    />
                    <span>{r.label}</span>
                  </ReasonItem>
                ))}
              </ReasonList>
              {reason === 'ETC' && (
                <DetailInput
                  placeholder="사유를 입력해 주세요 (개인정보는 입력하지 마세요)"
                  value={detail}
                  maxLength={300}
                  onChange={(e) => setDetail(e.target.value)}
                />
              )}
            </Content>
            <ContentRow alignItems="center" justifyContent="center" padding="20px 0">
              <CMButton
                onClick={onClose}
                backgroundColor="#fff"
                borderRadius={51}
                borderColor="#fff"
                color="#FF6969"
                hoverColor="#FF6969"
                style={{ marginRight: '10px' }}
              >
                취소
              </CMButton>
              <CMButton
                onClick={() => {
                  if (reason) {
                    setError('');
                    setStep('confirm');
                  }
                }}
                borderRadius={51}
                backgroundColor={reason ? '#FF6969' : '#ffb3b3'}
                borderColor={reason ? '#FF6969' : '#ffb3b3'}
                color="#fff"
              >
                다음
              </CMButton>
            </ContentRow>
          </>
        )}

        {step === 'confirm' && (
          <>
            <Content>
              <ConfirmText>정말 탈퇴하시겠어요?</ConfirmText>
              <Description>
                이 작업은 <strong>되돌릴 수 없습니다.</strong> 확인을 누르면
                개인정보가 즉시 파기됩니다.
              </Description>
              {error && <ErrorText>{error}</ErrorText>}
            </Content>
            <ContentRow alignItems="center" justifyContent="center" padding="20px 0">
              <CMButton
                onClick={() => {
                  if (!submitting) {
                    setError('');
                    setStep('form');
                  }
                }}
                backgroundColor="#fff"
                borderRadius={51}
                borderColor="#fff"
                color="#FF6969"
                hoverColor="#FF6969"
                style={{ marginRight: '10px' }}
              >
                이전
              </CMButton>
              <CMButton
                onClick={handleSubmit}
                borderRadius={51}
                backgroundColor={submitting ? '#ffb3b3' : '#FF6969'}
                borderColor={submitting ? '#ffb3b3' : '#FF6969'}
                color="#fff"
              >
                {submitting ? '처리 중…' : '탈퇴하기'}
              </CMButton>
            </ContentRow>
          </>
        )}

        {step === 'done' && (
          <>
            <Content>
              <ConfirmText>탈퇴가 완료되었습니다.</ConfirmText>
              <Description>
                그동안 피플을 이용해 주셔서 감사합니다.
              </Description>
            </Content>
            <ContentRow alignItems="center" justifyContent="center" padding="20px 0">
              <CMButton
                onClick={onDone}
                borderRadius={51}
                backgroundColor="#FF6969"
                borderColor="#FF6969"
                color="#fff"
              >
                확인
              </CMButton>
            </ContentRow>
          </>
        )}
      </Container>
    </ModalContainer>
  );
};

const HeaderTitle = styled(CMText)``;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 12px 24px;
  gap: 16px;
`;

const Description = styled.p`
  font-size: 14px;
  line-height: 1.5;
  color: ${({ theme }) => theme.commonColor.gray005};
`;

const ConfirmText = styled.p`
  font-size: 16px;
  font-weight: 700;
  color: #222;
`;

const ErrorText = styled.p`
  font-size: 13px;
  color: #ff4651;
`;

const ReasonList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ReasonItem = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
  cursor: pointer;
`;

const DetailInput = styled.textarea`
  width: 100%;
  min-height: 72px;
  padding: 10px;
  border: 1px solid #dddddd;
  border-radius: 10px;
  resize: none;
  font-size: 14px;
`;

export default WithdrawalModal;
